import { CSSProperties, useEffect, useState } from 'react'
import { usePrefersReducedMotion } from '@/components/landing/useLandingEffects'
import { GTDialogHeading } from '@/components/ui/gt-dialog'
import { cn } from '@/lib/utils'
import { Check, Sparkles } from 'lucide-react'
import {
    PLAN_DRAFTING_STAGES,
    getPlanDraftingStatus,
    planDraftingMotionMode,
    startPlanDraftingClock,
} from './PlanDraftingState.logic'

interface PlanDraftingStateProps {
    title: string
    subtitle: string
    className?: string
}

const SKELETON_ITEM_ROWS = [
    { width: 'w-10/12', hasMeta: true },
    { width: 'w-8/12', hasMeta: true },
    { width: 'w-9/12', hasMeta: false },
    { width: 'w-7/12', hasMeta: false },
]

const shimmerKeyframes = `
@keyframes plan-drafting-sweep {
    0% { transform: translateX(-120%); opacity: 0.45; }
    45% { opacity: 1; }
    100% { transform: translateX(320%); opacity: 0.45; }
}`

const PlanDraftingState = ({ title, subtitle, className }: PlanDraftingStateProps) => {
    const reduceMotion = usePrefersReducedMotion()
    const motionMode = planDraftingMotionMode(reduceMotion)
    const [status, setStatus] = useState(() => getPlanDraftingStatus(0))
    const isAnimated = motionMode === 'animated'

    useEffect(() => startPlanDraftingClock(setStatus), [])

    const rowClass = () =>
        cn(
            'relative overflow-hidden rounded-lg bg-muted/70',
            isAnimated && 'animate-in fade-in-0 slide-in-from-bottom-1 duration-300'
        )
    const delayedRowStyle = (delayMs: number): CSSProperties | undefined =>
        isAnimated ? { animationDelay: `${delayMs}ms`, animationFillMode: 'both' } : undefined

    return (
        <div data-testid="plan-drafting-state" data-motion={motionMode} className={cn('space-y-5', className)}>
            {isAnimated && <style>{shimmerKeyframes}</style>}
            <div className="space-y-3">
                <GTDialogHeading title={title} subtitle={subtitle} />
                <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/35 px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2 text-body-sm text-muted-foreground">
                        <Sparkles className="size-4 shrink-0 text-primary" />
                        <span
                            data-testid="plan-drafting-stage-label"
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                            className="truncate"
                        >
                            {status.message}
                        </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5" aria-hidden="true">
                        {PLAN_DRAFTING_STAGES.map((stage, index) => {
                            const complete = index < status.completedStageCount || status.isLongWait
                            const current = index === status.stageIndex && !status.isLongWait
                            return (
                                <span
                                    key={stage}
                                    className={cn(
                                        'grid size-4 place-items-center rounded-full border text-[10px] transition-colors duration-200',
                                        complete
                                            ? 'border-primary bg-primary text-primary-foreground'
                                            : current
                                            ? 'border-primary/60 bg-primary/10 text-primary'
                                            : 'border-border bg-background text-muted-foreground'
                                    )}
                                >
                                    {complete ? <Check className="size-3" /> : ''}
                                </span>
                            )
                        })}
                    </div>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                        data-testid={isAnimated ? 'plan-drafting-shimmer' : undefined}
                        className={cn('h-full rounded-full bg-primary/70', isAnimated ? 'w-1/3' : 'w-full opacity-40')}
                        style={isAnimated ? { animation: 'plan-drafting-sweep 1.8s ease-in-out infinite' } : undefined}
                    />
                </div>
            </div>

            <div className="space-y-2.5">
                <div className={rowClass()} style={delayedRowStyle(40)}>
                    <div className="h-5 w-7/12 rounded-md bg-background/60" />
                    <div className="mt-2 flex gap-2">
                        <div className="h-6 w-24 rounded-md bg-background/60" />
                        <div className="h-6 w-28 rounded-md bg-background/60" />
                    </div>
                </div>
                <div className="flex gap-1">
                    {[0, 1].map((phase) => (
                        <div
                            key={phase}
                            style={{ flexGrow: phase === 0 ? 3 : 2 }}
                            className="h-12 min-w-0 basis-0 rounded-md bg-muted/70"
                        />
                    ))}
                </div>
                <div className="space-y-1">
                    {SKELETON_ITEM_ROWS.map((row, rowIndex) => (
                        <div key={rowIndex} className={rowClass()} style={delayedRowStyle(120 + rowIndex * 70)}>
                            <div className="flex items-center gap-3 px-1 py-1.5">
                                <span className="size-4 shrink-0 rounded-sm border border-border bg-background/70" />
                                <span className={cn('h-4 min-w-0 rounded bg-background/60', row.width)} />
                                {row.hasMeta && <span className="h-6 w-20 shrink-0 rounded-md bg-background/60" />}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default PlanDraftingState
